import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addMenuHeaderMember, deleteMenuHeaderMember, updateMenu } from "@/sideEffects/api";
import { MenuDetails, MenuHeaderMember } from "@/lib/types";
import { FormSchemaType, MenuMetaForm } from "../form/MenuMetaForm";

interface Props {
  menuDetails: MenuDetails;
  menuHeaderMember?: MenuHeaderMember;
}

export const UpdateMenuMetaForm: React.FC<Props> = ({ menuDetails, menuHeaderMember }) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const updateMenuMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      await updateMenu({
        id: menuDetails.id,
        menuName: values.menuName,
        description: values.description ?? "",
        order: values.order,
        menuIcon: values.menuIcon ?? "",
        path: values.path,
        adminOnly: values.adminOnly ?? false,
      });

      if (menuHeaderMember) {
        await deleteMenuHeaderMember(menuHeaderMember.id);
      }

      if (values.menuHeaderId) {
        await addMenuHeaderMember(values.menuHeaderId, menuDetails.id);
      }

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getMenu"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getMenuDetails"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  return (
    <div>
      <MenuMetaForm
        buttonText={t("common.update")}
        onSubmit={(values) => updateMenuMutation.mutate(values)}
        isLoading={updateMenuMutation.isPending}
        defaultValues={{
          menuName: menuDetails.menuName,
          description: menuDetails.description ?? "",
          order: menuDetails.order.toString(),
          menuIcon: menuDetails.menuIcon,
          path: menuDetails.path,
          adminOnly: menuDetails.adminOnly,
          menuHeaderId: menuHeaderMember?.menuHeader_Id || 0,
        }}
      />
    </div>
  );
};
